// ============================================================================
// EcgCaptureQualityPanel.tsx — предупреждение «умной съёмки»: показывает
// метрики кадра и подсказку, предлагает переснять или отправить как есть.
// Не блокирует отправку — решение всегда за пользователем.
// ============================================================================

import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ECG_CAPTURE_HINT, type EcgCaptureAssessment } from '@/lib/ecgImageQuality';

interface Props {
    assessment: EcgCaptureAssessment | null;
    loading: boolean;
    error: string;
    onRetake: () => void;
    onSendAnyway: () => void;
}

function metricsLine(assessment: EcgCaptureAssessment): string {
    const overexposed = Math.round(assessment.overexposedRatio * 100);
    const content = Math.round(assessment.contentRatio * 100);
    const brightness = Math.round(assessment.brightness);
    return `резкость ${assessment.sharpnessScore.toFixed(2)} · яркость ${brightness} · пересветы ${overexposed}% · лист ${content}% кадра`;
}

export function EcgCaptureQualityPanel({ assessment, loading, error, onRetake, onSendAnyway }: Props) {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Проверяю качество снимка…
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800">
                Не удалось оценить качество снимка: {error}. Отправить его всё равно можно.
            </div>
        );
    }

    if (!assessment) return null;

    if (assessment.acceptable) {
        return (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Качество съёмки достаточное: {metricsLine(assessment)}.</span>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-amber-500/60 bg-amber-500/10 px-3 py-3 space-y-2">
            <div className="flex items-start gap-2 text-sm font-medium text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <span>Снимок может оказаться нечитаемым</span>
            </div>
            <ul className="list-disc pl-5 text-xs text-amber-900 space-y-0.5">
                {assessment.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                ))}
            </ul>
            <div className="text-xs text-amber-900">{ECG_CAPTURE_HINT}</div>
            <div className="text-[11px] text-amber-800">{metricsLine(assessment)}</div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7" onClick={onRetake}>
                    Переснять
                </Button>
                <Button size="sm" className="h-7" onClick={onSendAnyway}>
                    Всё равно отправить
                </Button>
            </div>
        </div>
    );
}
